<script setup lang="ts">
import { computed, inject, useId, useSlots, useTemplateRef } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import FeatureHighlightLabel from "@/components/ui/FeatureHighlight/FeatureHighlightLabel.vue";
import type { ListItemVariant } from "@/components/ui/ListItem/ListItem.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { ClickableProps } from "@/composables/Props";
import { useFeatureHighlight } from "@/composables/useFeatureHighlight";
import type { FeatureHighlightId } from "@/model/featureHighlight";

import { menuListConfigKey, menuListItemsContextKey } from "./injectKeys";

const elRef = useTemplateRef<HTMLElement>("elRef");

const props = defineProps<
  ClickableProps & {
    as?: "li" | "div" | "button" | "a";
    static?: boolean; // Non-interactive, but no visual different
    disabled?: boolean; // Non-interactive and looks 'disabled'
    selected?: boolean;
    variant?: ListItemVariant; // "cards" variant has no set height
    badge?: string; // Optional badge text (e.g., "2/3 selected")
    tooltip?: string; // Tooltip message shown on hover
    description?: string; // Accessible description, not shown
    role?: string; // ARIA role override
    highlight?: FeatureHighlightId; // Only the "New" label: a list row shows no description or learnMore
  }
>();

const descriptionId = useId();
const listConfig = inject(menuListConfigKey, undefined);
const itemsContext = inject(menuListItemsContextKey, undefined);
const slots = useSlots();
// a default slot replaces the text the highlight label is drawn from
const { shown: shownHighlight, markUsed: markHighlightUsed } = useFeatureHighlight(() =>
  props.disabled || props.static || slots.default ? undefined : props.highlight,
);

// Tippy derives text from the DOM to handle text passed as a prop or slot
const fullText = (el: Element) => el.textContent?.trim() ?? "";

const itemRole = computed(() => {
  if (props.role) return props.role;
  if (itemsContext?.role === "menu") return "menuitem";
  return "option";
});

defineExpose({ focus: () => void elRef.value?.focus() });
</script>

<template>
  <component
    :is="as || 'li'"
    ref="elRef"
    :class="[
      'menu-list-item',
      {
        static,
        disabled,
        selectable: listConfig?.selectable,
        selected,
        multiselect: listConfig?.multiselect,
      },
    ]"
    :role="itemRole"
    :aria-selected="itemRole === 'menuitem' ? undefined : !!selected"
    :aria-disabled="!!(static || disabled)"
    :aria-describedby="description ? descriptionId : undefined"
    tabindex="-1"
    @click="markHighlightUsed"
  >
    <span v-if="description" :id="descriptionId" hidden>{{ description }}</span>
    <BaseTooltip :content="tooltip" position="right" class="tooltip-wrapper" show-when-clipped>
      <div class="wrapper">
        <div v-if="$slots.before" class="before">
          <slot name="before" />
        </div>
        <div v-else-if="icon" class="before">
          <SvgIcon :name="icon" />
        </div>

        <!-- Inner tooltip shows the full text of truncated items,
             and only renders when there is no outer tooltip -->
        <BaseTooltip
          :content="tooltip ? undefined : fullText"
          only-show-on-overflow
          show-when-clipped
          :class="['content', variant || 'default']"
        >
          <slot v-if="$slots.default" />
          <FeatureHighlightLabel v-else-if="shownHighlight" :text="text!" />
          <span v-else>{{ text }}</span>
        </BaseTooltip>

        <span v-if="listConfig?.multiselect && listConfig?.selectable && badge" class="badge">
          {{ badge }}
        </span>

        <div v-if="$slots.after" class="after">
          <slot name="after" />
        </div>
        <div v-else-if="shortcut" class="after">
          <span class="btn">{{ shortcut }}</span>
        </div>

        <SvgIcon
          v-if="listConfig?.multiselect && listConfig?.selectable && selected && !badge"
          class="selected-icon"
          name="system/check"
          width="16"
          height="16"
        />
      </div>
    </BaseTooltip>
  </component>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/utils";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/shortcut";

.menu-list-item {
  flex-shrink: 0;
  outline: revert;
  padding: 2px 4px;

  // Tooltip wrapper should not affect layout (override BaseTooltip's inline-block)
  :deep(.tooltip-wrapper.base-tooltip) {
    display: block;
    width: 100%;
  }

  &.static {
    pointer-events: none;
  }

  &.disabled {
    cursor: not-allowed;
    color: colors.$text-disabled;

    :deep(.tooltip-wrapper.base-tooltip) {
      cursor: not-allowed;
    }

    .wrapper {
      cursor: not-allowed;
      color: colors.$text-disabled;

      .before,
      .after {
        color: colors.$text-disabled;
      }
    }
  }

  .wrapper {
    cursor: pointer;
    height: 36px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-radius: 4px;
    overflow: hidden;
    color: colors.$text;

    .before,
    .after {
      color: colors.$text-subtle;
      flex-shrink: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .before {
      min-width: 20px;
      min-height: 20px;
    }

    .after {
      @include typography.small;

      min-width: 16px;
      min-height: 16px;
    }

    .btn {
      @include shortcut.key;
    }
  }

  :focus-visible,
  &:hover:not(.disabled) {
    .wrapper {
      background-color: colors.$background-grey;

      .before,
      .after {
        color: colors.$text;
      }
    }
  }

  &:active:not(.multiselect, .disabled) {
    .wrapper {
      background-color: colors.$main;
      color: colors.$text-inverse;

      .before,
      .after {
        color: colors.$text-inverse;
      }
    }
  }

  &.selected.selectable:not(.multiselect) {
    .wrapper {
      background-color: colors.$main;
      color: colors.$text-inverse;

      .before {
        color: colors.$text-inverse;
      }

      .after {
        color: colors.$text-subtle-inverse;
      }
    }
  }

  .content {
    @include typography.medium;
    @include utils.ellipsis;

    display: block;
    flex-grow: 1;
  }

  .selected-icon {
    color: colors.$alternative-blue;
    flex-shrink: 0;
  }

  .badge {
    @include typography.extra-small("semi-bold");

    flex-shrink: 0;
    padding: 2px 4px;
    color: colors.$text;
    background-color: colors.$alpha-5;
    border-radius: 4px;
  }
}
</style>
